import { useState, useEffect, useRef } from 'react';
import { db } from '../../../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useActiveFund } from '../../../hooks/useActiveFund';
import { useAuth } from '../../../hooks/useAuth';

// FundSwitcher renders in the top navigation bar.
// - Admin with no fund_access: "All Funds" static label.
// - Single fund: static label, no dropdown (no implied choice).
// - Multi-fund: clickable dropdown to switch between accessible funds.
// - If active fund has has_streams: true, shows a secondary stream selector.
export default function FundSwitcher() {
  const { activeFundId, activeStreamId, setActiveFund, clearActiveFund, fundAccess } = useActiveFund();
  const { userProfile } = useAuth();
  const currentRoles = userProfile?.roles ||
    (userProfile?.role ? [userProfile.role] : []);
  const isAdmin = currentRoles.includes('admin');
  const [funds, setFunds] = useState([]);
  const [streams, setStreams] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Fetch fund details.
  // - Admins: fetch ALL funds (regardless of fund_access) so they can switch to
  //   any fund, sorted by short_name/name.
  // - Non-admins: EXISTING behaviour — skip when fund_access is empty; query the
  //   accessible funds and preserve the fund_access ordering.
  useEffect(() => {
    if (isAdmin) {
      getDocs(collection(db, 'funds')).then(snap => {
        const sorted = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) =>
            (a.short_name || a.name || '').localeCompare(b.short_name || b.name || ''));
        setFunds(sorted);
      }).catch(() => {});
      return;
    }
    if (!fundAccess.length) return;
    getDocs(
      query(collection(db, 'funds'), where('__name__', 'in', fundAccess))
    ).then(snap => {
      const sorted = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => fundAccess.indexOf(a.id) - fundAccess.indexOf(b.id));
      setFunds(sorted);
    }).catch(() => {});
  }, [fundAccess.join(','), isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch streams for the active fund if it has streams
  useEffect(() => {
    const activeFund = funds.find(f => f.id === activeFundId);
    if (!activeFund?.has_streams) { setStreams([]); return; }
    getDocs(collection(db, 'funds', activeFundId, 'streams'))
      .then(snap => setStreams(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setStreams([]));
  }, [activeFundId, funds]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeFund = funds.find(f => f.id === activeFundId);
  const multipleStreams = streams.length > 1;

  // Admin: real switcher across ALL funds. Handled BEFORE the non-admin
  // `!activeFund` guard because an admin on "All Funds" legitimately has
  // activeFundId null (activeFund undefined) and must still see the switcher.
  // Applies to any admin, including one who also has fund_access populated
  // (product decision: admins can switch to any fund).
  if (isAdmin) {
    const adminLabel = activeFund ? activeFund.short_name : 'All Funds';
    return (
      <div ref={ref} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 600,
              color: 'var(--color-accent)',
              padding: '4px 10px',
              borderRadius: 6,
              background: 'rgba(201,149,42,0.12)',
              border: '1px solid rgba(201,149,42,0.3)',
              cursor: 'pointer',
            }}
          >
            {adminLabel}
            <span style={{ fontSize: 10, opacity: 0.8 }}>{open ? '▲' : '▼'}</span>
          </button>
          {open && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 4,
              background: 'var(--color-card)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-md)',
              minWidth: 160, zIndex: 200, maxHeight: 320, overflowY: 'auto',
            }}>
              {/* All Funds — clears the active fund (activeFundId → null) */}
              <button
                onClick={() => { clearActiveFund(); setOpen(false); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 14px', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 13,
                  fontWeight: !activeFundId ? 700 : 400,
                  color: !activeFundId ? 'var(--color-accent)' : 'var(--color-text)',
                  borderBottom: '1px solid var(--color-border-light)',
                }}
              >
                All Funds
              </button>
              {funds.map(f => (
                <button
                  key={f.id}
                  onClick={() => { setActiveFund(f.id, null); setOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 14px', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: 13,
                    fontWeight: f.id === activeFundId ? 700 : 400,
                    color: f.id === activeFundId ? 'var(--color-accent)' : 'var(--color-text)',
                    borderBottom: '1px solid var(--color-border-light)',
                  }}
                >
                  {f.short_name}
                  <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginLeft: 6 }}>
                    {f.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        {activeFund && multipleStreams && (
          <StreamSelector
            streams={streams}
            activeStreamId={activeStreamId}
            onSelect={streamId => setActiveFund(activeFundId, streamId)}
          />
        )}
      </div>
    );
  }

  if (!activeFund) return null; // funds not yet loaded

  // Single-fund user: static text, no dropdown
  if (fundAccess.length <= 1) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 13, fontWeight: 600,
          color: 'var(--color-accent)',
          padding: '4px 10px',
          borderRadius: 6,
          background: 'rgba(201,149,42,0.12)',
          border: '1px solid rgba(201,149,42,0.3)',
        }}>
          {activeFund.short_name}
        </span>
        {multipleStreams && (
          <StreamSelector
            streams={streams}
            activeStreamId={activeStreamId}
            onSelect={streamId => setActiveFund(activeFundId, streamId)}
          />
        )}
      </div>
    );
  }

  // Multi-fund user: dropdown
  return (
    <div ref={ref} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600,
            color: 'var(--color-accent)',
            padding: '4px 10px',
            borderRadius: 6,
            background: 'rgba(201,149,42,0.12)',
            border: '1px solid rgba(201,149,42,0.3)',
            cursor: 'pointer',
          }}
        >
          {activeFund.short_name}
          <span style={{ fontSize: 10, opacity: 0.8 }}>{open ? '▲' : '▼'}</span>
        </button>
        {open && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4,
            background: 'var(--color-card)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-md)',
            minWidth: 160, zIndex: 200,
          }}>
            {funds.map(f => (
              <button
                key={f.id}
                onClick={() => { setActiveFund(f.id, null); setOpen(false); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 14px', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 13,
                  fontWeight: f.id === activeFundId ? 700 : 400,
                  color: f.id === activeFundId ? 'var(--color-accent)' : 'var(--color-text)',
                  borderBottom: '1px solid var(--color-border-light)',
                }}
              >
                {f.short_name}
                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginLeft: 6 }}>
                  {f.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {multipleStreams && (
        <StreamSelector
          streams={streams}
          activeStreamId={activeStreamId}
          onSelect={streamId => setActiveFund(activeFundId, streamId)}
        />
      )}
    </div>
  );
}

function StreamSelector({ streams, activeStreamId, onSelect }) {
  return (
    <select
      value={activeStreamId || ''}
      onChange={e => onSelect(e.target.value || null)}
      style={{
        fontSize: 12, padding: '4px 8px', borderRadius: 6,
        border: '1px solid rgba(201,149,42,0.3)',
        background: 'rgba(201,149,42,0.08)',
        color: 'var(--color-accent)', cursor: 'pointer',
      }}
    >
      <option value="">All streams</option>
      {streams.map(s => (
        <option key={s.id} value={s.id}>{s.short_name}</option>
      ))}
    </select>
  );
}
